
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, X } from 'lucide-react';
import { format } from 'date-fns';

interface DocumentViewerProps {
  document: any;
  isOpen: boolean;
  onClose: () => void;
  onDownload: () => void;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document,
  isOpen,
  onClose,
  onDownload
}) => {
  const renderDocumentPreview = () => {
    if (document.fileType.startsWith('image/')) {
      return (
        <div className="text-center">
          <img 
            src={document.fileData} 
            alt={document.name}
            className="max-w-full max-h-96 mx-auto rounded-lg shadow-sm"
          />
        </div>
      );
    } else if (document.fileType === 'application/pdf') {
      return (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">PDF Preview</p>
          <p className="text-sm text-gray-500 mt-2">
            Click download to view the full PDF file
          </p>
        </div>
      );
    } else if (document.fileType.startsWith('text/')) {
      try {
        const textContent = atob(document.fileData.split(',')[1]);
        return (
          <div className="bg-gray-50 p-4 rounded-lg">
            <pre className="whitespace-pre-wrap text-sm max-h-96 overflow-y-auto">
              {textContent}
            </pre>
          </div>
        );
      } catch (error) {
        return (
          <div className="text-center p-8 bg-gray-50 rounded-lg">
            <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Text file preview unavailable</p>
          </div>
        );
      }
    } else {
      return (
        <div className="text-center p-8 bg-gray-50 rounded-lg">
          <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">Preview not available</p>
          <p className="text-sm text-gray-500 mt-2">
            Download the file to view its contents
          </p>
        </div>
      );
    }
  };

  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case 'certificate': return 'bg-green-100 text-green-800';
      case 'academic': return 'bg-blue-100 text-blue-800';
      case 'id': return 'bg-purple-100 text-purple-800';
      case 'general': return 'bg-gray-100 text-gray-800';
      default: return 'bg-orange-100 text-orange-800';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {document.name}
            </DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={onDownload}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Document Metadata */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium text-gray-600">Category</p>
              <Badge className={getCategoryBadgeColor(document.category)}>
                {document.category.charAt(0).toUpperCase() + document.category.slice(1)}
              </Badge>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">File Size</p>
              <p className="font-medium">{formatFileSize(document.fileSize)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">File Type</p>
              <p className="font-medium">{document.fileType}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Upload Date</p>
              <p className="font-medium">{format(new Date(document.uploadDate), 'MMM dd, yyyy')}</p>
            </div>
          </div>

          {/* Description */}
          {document.description && (
            <div>
              <h3 className="text-lg font-medium mb-2">Description</h3>
              <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{document.description}</p>
            </div>
          )}

          {/* Document Preview */}
          <div>
            <h3 className="text-lg font-medium mb-2">Preview</h3>
            {renderDocumentPreview()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
